import { noop } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    type GetConstructorLatestVersionContentResponse,
    type GetConstructorTemplatePreviewResponse,
    type GetConstructorVersionContentResponse,
    getOpenApiCreateSectionTemplateUrl,
    getOpenApiDeleteSectionTemplateUrl,
    getOpenApiGetConstructorLatestContentUrl,
    getOpenApiGetConstructorVersionContentUrl,
    getOpenApiGetConstructorVersionsUrl,
    getOpenApiGetSectionTemplatesUrl,
    getOpenApiGetSectionTemplateVersionsUrl,
    getOpenApiGetSectionTemplateVersionUrl,
    getOpenApiUpdateConstructorContentUrl,
    getOpenApiUpdateSectionTemplateUrl,
    OpenApiCreateSectionTemplateMethod,
    type OpenApiCreateSectionTemplateRequest,
    type OpenApiCreateSectionTemplateResponseSuccess,
    OpenApiDeleteSectionTemplateMethod,
    type OpenApiDeleteSectionTemplatePathParameters,
    type OpenApiDeleteSectionTemplateResponseSuccess,
    OpenApiGetConstructorLatestContentMethod,
    OpenApiGetConstructorVersionContentMethod,
    type OpenApiGetConstructorVersionContentPathParameters,
    OpenApiGetConstructorVersionsMethod,
    type OpenApiGetConstructorVersionsResponseSuccess,
    OpenApiGetSectionTemplatesMethod,
    type OpenApiGetSectionTemplatesRequest,
    type OpenApiGetSectionTemplatesResponseSuccess,
    OpenApiGetSectionTemplateVersionMethod,
    type OpenApiGetSectionTemplateVersionPathParameters,
    type OpenApiGetSectionTemplateVersionResponseSuccess,
    OpenApiGetSectionTemplateVersionsMethod,
    type OpenApiGetSectionTemplateVersionsPathParameters,
    type OpenApiGetSectionTemplateVersionsResponseSuccess,
    OpenApiPreviewConstructorWithTemplateMethod,
    type OpenApiPreviewConstructorWithTemplateSearchParams,
    OpenApiUpdateConstructorContentMethod,
    OpenApiUpdateSectionTemplateMethod,
    type OpenApiUpdateSectionTemplatePathParameters,
    type OpenApiUpdateSectionTemplateRequest,
    type OpenApiUpdateSectionTemplateResponseSuccess,
    type UpdateConstructorContentRequest,
    type UpdateConstructorContentResponse,
} from '#shared/api/schema';

export const ConstructorTag = 'Constructor' as const;

export const api = createApi({
    name: 'entities/constructor/api/constructor/api',
    tagTypes: [ConstructorTag],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getAllVersions: builder.query<OpenApiGetConstructorVersionsResponseSuccess, {}>({
                query: () => {
                    return {
                        url: getOpenApiGetConstructorVersionsUrl(),
                        method: OpenApiGetConstructorVersionsMethod,
                    };
                },
            }),
            getVersionContent: builder.query<
                GetConstructorVersionContentResponse,
                OpenApiGetConstructorVersionContentPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiGetConstructorVersionContentUrl(params),
                        method: OpenApiGetConstructorVersionContentMethod,
                    };
                },
            }),
            getLatestVersion: builder.query<GetConstructorLatestVersionContentResponse, {}>({
                query: () => {
                    return {
                        url: getOpenApiGetConstructorLatestContentUrl(),
                        method: OpenApiGetConstructorLatestContentMethod,
                    };
                },
            }),
            updateConstructorContent: builder.mutation<
                UpdateConstructorContentResponse,
                UpdateConstructorContentRequest
            >({
                query: (payload) => {
                    return {
                        url: getOpenApiUpdateConstructorContentUrl(),
                        method: OpenApiUpdateConstructorContentMethod,
                        body: payload,
                    };
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    api.endpoints.getAllVersions.updateQueryData({}, (draft) => {
                        return {
                            versions: [data.version, ...draft.versions],
                        };
                    });
                },
            }),
            getConstructorTemplatePreview: builder.query<
                GetConstructorTemplatePreviewResponse,
                OpenApiPreviewConstructorWithTemplateSearchParams
            >({
                query: ({ template_id }) => {
                    return {
                        url: `/constructor/content/template?template_id=${template_id}`,
                        method: OpenApiPreviewConstructorWithTemplateMethod,
                    };
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    api.endpoints.getAllVersions.updateQueryData({}, (draft) => {
                        return {
                            versions: [data.version, ...draft.versions],
                        };
                    });
                },
            }),
            getConstructorTemplateSections: builder.query<
                OpenApiGetSectionTemplatesResponseSuccess,
                OpenApiGetSectionTemplatesRequest
            >({
                query: () => {
                    return {
                        url: getOpenApiGetSectionTemplatesUrl(),
                        method: OpenApiGetSectionTemplatesMethod,
                    };
                },
                keepUnusedDataFor: 60 * 10,
            }),
            updateConstructorTemplateSection: builder.mutation<
                OpenApiUpdateSectionTemplateResponseSuccess,
                { params: OpenApiUpdateSectionTemplatePathParameters; payload: OpenApiUpdateSectionTemplateRequest }
            >({
                query: ({ params, payload }) => {
                    return {
                        url: getOpenApiUpdateSectionTemplateUrl(params),
                        method: OpenApiUpdateSectionTemplateMethod,
                        body: payload,
                    };
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    api.endpoints.getConstructorTemplateSections.updateQueryData(undefined, (draft) =>
                        draft.map((section) => {
                            if (section.id === data.id) {
                                return data;
                            }

                            return section;
                        })
                    );
                    api.endpoints.getSectionTemplateVersions.updateQueryData({ id: data.id }, (draft) => {
                        return {
                            ...draft,
                            versions: [data.version, ...draft.versions],
                        };
                    });
                },
            }),
            addConstructorTemplateSection: builder.mutation<
                OpenApiCreateSectionTemplateResponseSuccess,
                OpenApiCreateSectionTemplateRequest
            >({
                query: (payload) => {
                    return {
                        url: getOpenApiCreateSectionTemplateUrl(),
                        method: OpenApiCreateSectionTemplateMethod,
                        body: payload,
                    };
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    const { data } = queryFulfilledResult;

                    api.endpoints.getConstructorTemplateSections.updateQueryData(undefined, (draft) => [
                        ...draft,
                        data,
                    ]);
                },
            }),
            deleteConstructorTemplateSection: builder.mutation<
                OpenApiDeleteSectionTemplateResponseSuccess,
                { params: OpenApiDeleteSectionTemplatePathParameters }
            >({
                query: ({ params }) => {
                    return {
                        url: getOpenApiDeleteSectionTemplateUrl(params),
                        method: OpenApiDeleteSectionTemplateMethod,
                    };
                },
                async onQueryStarted({ params: { id } }, { queryFulfilled }) {
                    const queryFulfilledResult = await queryFulfilled.catch(noop);

                    if (queryFulfilledResult === undefined) {
                        return;
                    }

                    api.endpoints.getConstructorTemplateSections.updateQueryData(undefined, (draft) =>
                        draft.filter((section) => section.id !== id)
                    );
                },
            }),
            getSectionTemplateVersions: builder.query<
                OpenApiGetSectionTemplateVersionsResponseSuccess,
                OpenApiGetSectionTemplateVersionsPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiGetSectionTemplateVersionsUrl(params),
                        method: OpenApiGetSectionTemplateVersionsMethod,
                    };
                },
            }),
            getSectionTemplateVersion: builder.query<
                OpenApiGetSectionTemplateVersionResponseSuccess,
                OpenApiGetSectionTemplateVersionPathParameters
            >({
                query: (params) => {
                    return {
                        url: getOpenApiGetSectionTemplateVersionUrl(params),
                        method: OpenApiGetSectionTemplateVersionMethod,
                    };
                },
            }),
        };
    },
});

export const useGetAllVersions = api.useGetAllVersionsQuery;

export const getVersion = api.endpoints.getVersionContent.query;

export const useConstructorGetLatestVersion = api.useGetLatestVersionQuery;

export const getLatestVersion = api.endpoints.getLatestVersion.query;

export const updateConstructorContent = api.endpoints.updateConstructorContent.mutate;

export const getConstructorTemplatePreview = api.endpoints.getConstructorTemplatePreview.query;

export const getConstructorTemplateSections = api.endpoints.getConstructorTemplateSections.query;

export const updateConstructorTemplateSection = api.endpoints.updateConstructorTemplateSection.mutate;

export const addConstructorTemplateSection = api.endpoints.addConstructorTemplateSection.mutate;

export const deleteConstructorTemplateSection = api.endpoints.deleteConstructorTemplateSection.mutate;

export const useGetSectionTemplateVersions = api.useGetSectionTemplateVersionsQuery;

export const useConstructorLazyGetSectionTemplateVersionQuery = api.useLazyGetSectionTemplateVersionQuery;
